import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Pack-variant volume tiers UI. Selection syncs to the hidden pack option
 * inside the product's variant-picker so Horizon morph/price updates run.
 *
 * @typedef {Object} VolumeTiersRefs
 */

/**
 * @extends {Component<VolumeTiersRefs>}
 */
class VolumeTiersComponent extends Component {
  /** @type {boolean} */
  #syncingFromPicker = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {Event} event
   */
  #onChange = (event) => {
    if (this.#syncingFromPicker) return;

    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || !target.matches('[data-volume-tier]')) {
      return;
    }

    if (target.getAttribute('aria-disabled') === 'true' || target.dataset.optionAvailable === 'false') {
      return;
    }

    this.#setSelectedOption(target);
    this.#syncVariantPicker(target);
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const html = event.detail.data.html;
    if (!(html instanceof Document || html instanceof Element)) return;

    const source = html.querySelector(`volume-tiers-component[data-product-id="${this.dataset.productId}"]`);
    if (!source) return;

    const newGrid = source.querySelector('.volume-tiers__grid');
    const currentGrid = this.querySelector('.volume-tiers__grid');
    if (!(newGrid instanceof Element) || !(currentGrid instanceof Element)) return;

    this.#syncingFromPicker = true;

    try {
      morph(currentGrid, newGrid);

      const checked = this.querySelector('[data-volume-tier]:checked');
      if (checked instanceof HTMLInputElement) {
        this.#setSelectedOption(checked);
      }
    } finally {
      this.#syncingFromPicker = false;
    }
  };

  /**
   * @param {HTMLInputElement} input
   */
  #setSelectedOption(input) {
    for (const option of this.querySelectorAll('.volume-tiers__option')) {
      option.classList.toggle('is-selected', option.contains(input));
    }
  }

  /**
   * @param {HTMLInputElement} tierInput
   */
  #syncVariantPicker(tierInput) {
    const optionValueId = tierInput.dataset.optionValueId;
    if (!optionValueId) return;

    const productId = this.dataset.productId;
    const picker = productId
      ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelector('variant-picker');

    if (!(picker instanceof HTMLElement)) return;

    const pickerInput = picker.querySelector(`input[data-option-value-id="${optionValueId}"]`);
    if (!(pickerInput instanceof HTMLInputElement)) return;

    if (pickerInput.checked) return;

    pickerInput.checked = true;
    pickerInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('volume-tiers-component')) {
  customElements.define('volume-tiers-component', VolumeTiersComponent);
}
